import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { AppLayout } from "@/components/shared/AppLayout";
import { LandingPage } from "@/pages/LandingPage";
import { OwnerRequestPage } from "@/pages/OwnerRequestPage";
import { OwnerDashboardPage } from "@/pages/OwnerDashboardPage";
import { AgentRegistrationPage } from "@/pages/AgentRegistrationPage";
import { AgentDashboardPage } from "@/pages/AgentDashboardPage";
import { AgentBrowsePage } from "@/pages/AgentBrowsePage";
import { InventoryPage } from "@/pages/InventoryPage";
import { AdminPage } from "@/pages/AdminPage";
import ResponseReviewPage from "@/pages/ResponseReviewPage";
import RequestDetailPage from "@/pages/RequestDetailPage";
import QRScanPage from "@/pages/QRScanPage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import ConsentHistoryPage from "@/pages/ConsentHistoryPage";
import BillingHistoryPage from "@/pages/BillingHistoryPage";
import ActiveLeadsPage from "@/pages/ActiveLeadsPage";
import { PaymentCallback } from "@/components/shared/PaymentCallback";

function ProtectedRoute({ children }: { children: React.ReactNode; allowedRoles?: string[] }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div className="flex items-center justify-center h-screen"><LoadingSpinner /></div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Role check disabled for testing:
  // if (allowedRoles && allowedRoles.length > 0 && (!user?.role || !allowedRoles.includes(user.role))) {
  //   return <Navigate to="/" replace />;
  // }
  return <>{children}</>;
}

function LoadingSpinner() {
  return (
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sun" />
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public */}
        <Route path="/" element={<LandingPage />} />

        {/* Owner Routes */}
        <Route path="/owner" element={<ProtectedRoute allowedRoles={["owner"]}><AppLayout /></ProtectedRoute>}>
          <Route index element={<OwnerDashboardPage />} />
          <Route path="new-request" element={<OwnerRequestPage />} />
          <Route path="requests/:id" element={<RequestDetailPage />} />
          <Route path="requests/:id/responses" element={<ResponseReviewPage />} />
          <Route path="consent-history" element={<ConsentHistoryPage />} />
        </Route>

        {/* Agent Routes */}
        <Route path="/agent" element={<ProtectedRoute allowedRoles={["agent"]}><AppLayout /></ProtectedRoute>}>
          <Route index element={<AgentDashboardPage />} />
          <Route path="register" element={<AgentRegistrationPage />} />
          <Route path="browse" element={<AgentBrowsePage />} />
          <Route path="scan" element={<QRScanPage />} />
          <Route path="subscription" element={<SubscriptionPage />} />
          <Route path="subscription/callback" element={<PaymentCallback redirectTo="/agent/subscription" />} />
          <Route path="boosts/callback" element={<PaymentCallback redirectTo="/agent" />} />
          <Route path="billing" element={<BillingHistoryPage />} />
          <Route path="leads" element={<ActiveLeadsPage />} />
          <Route path="consent-history" element={<ConsentHistoryPage />} />
        </Route>

        {/* Admin Routes */}
        <Route path="/admin" element={<ProtectedRoute allowedRoles={["admin"]}><AppLayout /></ProtectedRoute>}>
          <Route index element={<AdminPage />} />
          <Route path="inventory" element={<InventoryPage />} />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
